import { ColumnDef, DefaultTData, DropdownOption, Row } from '@dbt-labs/sourdough';

import { PaginatedTable, PaginationType } from '@/components/PaginatedTable';
import { getNameWithAccessLinkCell } from '@/components/Table/LinkCell';
import { TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useFavoriteProjectIds } from '@/hooks/admin/favoriteProjects';
import { useLinkToExplorer } from '@/hooks/links';
import { FavoriteProjectToggle } from './FavoriteToggle';

interface MeshProjectTableProps {
  paginationType: PaginationType;
  pageOptions: DropdownOption[];
  pageSize: number;
  isLoading: boolean;
  hasMoreResults: boolean;
  allProjects: ProjectEntry[] | undefined;
  fetchMore: () => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalCount: number;
  totalPages: number;
}

export type ProjectEntry = {
  name?: string | null;
  dbtCloudProject?: string;
  dbtCoreProject?: string | null;
  projectId: number;
  defaultEnvironmentId?: number | null;
  projectSummary?: {
    publicModels: number | undefined;
    totalModels: number | undefined;
    metrics: number | undefined;
  };
  hasAccess: boolean;
};

type ProjectEntryRow = ProjectEntry & DefaultTData;

export const getColumns = (
  accountId: number,
  getProjectLink: any,
  favoriteProjectIds: number[],
): ColumnDef<ProjectEntryRow, any>[] => [
  {
    header: '',
    id: 'is_favorite',
    enableSorting: false,
    minSize: 48,
    size: 48,
    maxSize: 48,
    cell: ({ row }: { row: Row<ProjectEntryRow> }) => {
      const projectId = row.original.projectId;
      return (
        <div className="pointer-events-auto flex items-center">
          <FavoriteProjectToggle
            isFavorite={favoriteProjectIds.includes(projectId)}
            projectId={projectId}
          />
        </div>
      );
    },
  },
  {
    header: 'Name',
    accessorFn: (row) => row.dbtCloudProject || row.dbtCoreProject,
    id: 'name',
    cell: getNameWithAccessLinkCell((row) =>
      getProjectLink({
        accountId,
        projectId: row.projectId,
        // Account level projects list only links to production
        environmentType: 'production',
      }),
    ),
  },
  {
    header: 'dbt project',
    accessorKey: 'dbtCoreProject',
    cell: TruncatedCell,
  },
  {
    header: 'Public models',
    accessorKey: 'projectSummary.publicModels',
    size: 60,
    minSize: 120,
  },
  {
    header: 'Models',
    accessorKey: 'projectSummary.totalModels',
    size: 60,
    minSize: 120,
  },
  {
    header: 'Metrics',
    accessorKey: 'projectSummary.metrics',
    size: 60,
    minSize: 120,
  },
];

export const MeshProjectTable: React.FC<MeshProjectTableProps> = ({
  paginationType,
  pageOptions,
  pageSize,
  isLoading,
  hasMoreResults,
  allProjects,
  fetchMore,
  currentPage,
  setCurrentPage,
  totalCount,
}) => {
  const { accountId } = useDbtCloudAccount();
  const getProjectLink = useLinkToExplorer('project');

  const { favoriteProjectIds } = useFavoriteProjectIds(accountId ?? 0);

  const pageInfo = {
    totalCount: null,
    hasNextPage: hasMoreResults,
    endCursor: null,
  };

  const pageDisplayLabel =
    allProjects?.length == null || pageInfo?.totalCount == null
      ? undefined
      : `Loaded ${allProjects.length} of ${pageInfo.totalCount}`;

  if (!accountId) {
    return null;
  }

  return (
    <PaginatedTable
      data={allProjects ?? []}
      paginationType={paginationType}
      rowHeight={40}
      isSortable={false}
      loadingRowCount={pageSize}
      isLoading={isLoading}
      maxResultCount={totalCount}
      pageSize={pageSize}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      emptyStateProps={{
        header: `No results`,
        helpText: `No results were found`,
      }}
      columns={getColumns(accountId, getProjectLink, favoriteProjectIds)}
      pagination={{
        selectedPageCount: pageOptions.find(({ value }) => value === pageSize),
        pageOptions,
        shouldShowLoadMore: !isLoading && pageInfo?.hasNextPage,
        hasMorePages: pageInfo?.hasNextPage,
        onLoadMore: fetchMore,
        isPageLoading: isLoading,
        pageDisplayLabel,
      }}
    />
  );
};
